// posthog-error-handler.ts
import { ErrorHandler, Injectable, Provider } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import posthog from 'posthog-js';

/* =========================
   CONFIG
   ========================= */
const FLUSH_INTERVAL_MS = 5000;       // aggregate window before sending
const MAX_TOTAL_PER_MIN = 80;         // global cap per 60s (across all error types)
const MAX_PER_FP_PER_MIN = 8;         // per-fingerprint cap per 60s
const COOLDOWN_MS = 30000;            // pause sending if global cap tripped
const SAMPLE_RATE_AFTER_CAP = 0.1;    // trickle 10% after per-fp cap
const EVENT_NAME = 'client_error';    // PostHog event name
const ENV = 'web';                    // tag your surface/app

/* =========================
   TYPES
   ========================= */
type HttpErrorObj = {
  message: string;
  status: number;
  url?: string;
  method?: string;
};

type WindowBin = { start: number; count: number };
type AggItem = { info: Info; count: number; firstTs: number; lastTs: number };

type Info = {
  message: string;
  name?: string;
  stack?: string;
  source?: string;
  url?: string;
  line: number | null;
  col: number | null;
  httpStatus?: number;
  httpUrl?: string;
  httpMethod?: string;
};

/* =========================
   ERROR HANDLER
   ========================= */
@Injectable({ providedIn: 'root' })
export class PostHogErrorHandler implements ErrorHandler {
  private q = new Map<string, AggItem>();           // fingerprint -> aggregate item
  private fpWindow = new Map<string, WindowBin>();  // fingerprint -> rolling minute window
  private globalWindow: WindowBin = { start: 0, count: 0 };
  private flushTimer: any = null;
  private cooldownUntil = 0;
  private warnedCooldown = false;

  public handleError(error: unknown): void {
    const extracted = this._extractError(error); // <- returns union type (TS2345-safe)
    if (!extracted) return;

    const info = this._toInfo(extracted);
    if (this._shouldIgnore(info)) return;

    const fp = this._fingerprint(info);
    const now = Date.now();

    // bump rolling windows
    this._bumpWindows(fp, now);

    // global cap -> cooldown
    if (this.globalWindow.count > MAX_TOTAL_PER_MIN) {
      this.cooldownUntil = now + COOLDOWN_MS;
    }
    if (now < this.cooldownUntil) {
      if (!this.warnedCooldown) {
        this.warnedCooldown = true;
        // eslint-disable-next-line no-console
        console.warn(
          '[ErrorHandler] cooling down error reporting for',
          Math.round((this.cooldownUntil - now) / 1000),
          's'
        );
      }
      return;
    } else {
      this.warnedCooldown = false;
    }

    // per-fingerprint cap -> sample
    const w = this.fpWindow.get(fp)!;
    if (w.count > MAX_PER_FP_PER_MIN) {
      if (Math.random() > SAMPLE_RATE_AFTER_CAP) return;
    }

    // aggregate
    const item = this.q.get(fp) ?? {
      info,
      count: 0,
      firstTs: now,
      lastTs: now,
    };
    item.count += 1;
    item.lastTs = now;
    this.q.set(fp, item);

    // schedule flush
    if (!this.flushTimer) {
      this.flushTimer = setTimeout(() => this.flush(), FLUSH_INTERVAL_MS);
    }
  }

  /* ========== FLUSH BATCH TO POSTHOG ========== */
  private flush() {
    this.flushTimer = null;
    if (!this.q.size) return;

    const batch = Array.from(this.q.values());
    this.q.clear();

    runOutsideAngular(() => {
      for (const it of batch) {
        const p = it.info;
        const props = {
          message: p.message,
          name: p.name,
          stack: p.stack,
          source: p.source,
          url: p.url,
          line: p.line,
          col: p.col,
          http_status: p.httpStatus ?? null,
          http_url: p.httpUrl ?? null,
          http_method: p.httpMethod ?? null,
          occurrences: it.count,
          first_ts: new Date(it.firstTs).toISOString(),
          last_ts: new Date(it.lastTs).toISOString(),
          env: ENV,
          user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        };

        try {
          // PostHog JS SDK: send custom event with properties
          posthog.capture(EVENT_NAME, props);
        } catch {
          // swallow telemetry errors
        }
      }
    });
  }

  /* ========== EXTRACTION & NORMALIZATION ========== */

  // TS2345 fix: return a specific union, not `unknown`
  protected _extractError(errorCandidate: unknown): string | Error | HttpErrorObj | null {
    const error = tryToUnwrapZonejsError(errorCandidate);

    if (error instanceof HttpErrorResponse) {
      return extractHttpModuleErrorObject(error);
    }

    if (typeof error === 'string' || isErrorOrErrorLikeObject(error)) {
      return error as string | Error;
    }

    return null;
  }

  private _toInfo(e: string | Error | HttpErrorObj): Info {
    // string
    if (typeof e === 'string') {
      return {
        message: e,
        name: 'Error',
        stack: undefined,
        source: 'runtime',
        url: this._page(),
        line: null,
        col: null,
      };
    }

    // Http error object
    if (isHttpErrorObj(e)) {
      return {
        message: e.message,
        name: 'HttpErrorResponse',
        stack: undefined,
        source: 'http',
        url: this._page(),
        line: null,
        col: null,
        httpStatus: e.status,
        httpUrl: e.url,
        httpMethod: e.method,
      };
    }

    // native Error
    const { message, name, stack } = e as Error;
    const sc = this._parseStack(stack);
    return {
      message,
      name,
      stack,
      source: sc?.source || 'runtime',
      url: this._page(),
      line: sc?.line ?? null,
      col: sc?.col ?? null,
    };
  }

  private _shouldIgnore(info: Info): boolean {
    const msg = (info.message || '').toLowerCase();

    // Common noisy/non-actionable cases
    if (msg.includes('cancelled') || msg.includes('canceled')) return true;
    if (msg.includes('chunkloaderror') || msg.includes('loading chunk')) return true;
    if (msg.includes('script error') && !info.stack) return true;
    if (info.httpStatus === 0) return true; // CORS/abort/offline

    return false;
    }

  private _fingerprint(info: Info): string {
    const base = [
      info.name || 'Error',
      (info.message || '').slice(0, 200),
      info.source || '',
      info.httpStatus ?? '',
      info.httpMethod ?? '',
      this._firstStackLine(info.stack) || '',
    ].join('|');
    // simple fast hash
    let h = 0;
    for (let i = 0; i < base.length; i++) h = ((h << 5) - h) + base.charCodeAt(i) | 0;
    return h.toString(36);
  }

  private _bumpWindows(fp: string, now: number) {
    // global
    if (!this.globalWindow.start || now - this.globalWindow.start >= 60000) {
      this.globalWindow.start = now; this.globalWindow.count = 0;
    }
    this.globalWindow.count++;

    // per-fingerprint
    const w = this.fpWindow.get(fp) ?? { start: 0, count: 0 };
    if (!w.start || now - w.start >= 60000) { w.start = now; w.count = 0; }
    w.count++;
    this.fpWindow.set(fp, w);
  }

  private _page(): string | undefined {
    try { return typeof location !== 'undefined' ? location.href : undefined; } catch { return undefined; }
  }

  private _parseStack(stack?: string | null): { source?: string; line?: number; col?: number } | null {
    if (!stack) return null;
    const m = stack.match(/(?:\(|@)(.*?):(\d+):(\d+)\)?/);
    if (!m) return null;
    return { source: m[1], line: parseInt(m[2], 10), col: parseInt(m[3], 10) };
  }

  private _firstStackLine(stack?: string | null): string | null {
    if (!stack) return null;
    const line = stack.split('\n').find(l => l.includes(':'));
    return line ?? null;
  }
}

/* =========================
   HELPERS
   ========================= */
function tryToUnwrapZonejsError(error: unknown): unknown | Error {
  return error && (error as { ngOriginalError: Error }).ngOriginalError
    ? (error as { ngOriginalError: Error }).ngOriginalError
    : error;
}

function isErrorOrErrorLikeObject(value: unknown): value is Error {
  if (value instanceof Error) return true;
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  return 'name' in value && 'message' in value; // stack may be missing in some envs
}

function isHttpErrorObj(v: any): v is HttpErrorObj {
  return v && typeof v.status === 'number' && typeof v.message === 'string';
}

function extractHttpModuleErrorObject(err: HttpErrorResponse): HttpErrorObj {
  const message =
    (isErrorOrErrorLikeObject(err.error) && (err.error as Error).message) ||
    (typeof err.error === 'string' && `Server returned code ${err.status} with body "${err.error}"`) ||
    err.message;

  // Angular doesn't expose method on HttpErrorResponse; keep best-effort
  const method = (err as any)?.method ?? (err as any)?.headers?.get?.(':method') ?? undefined;
  return { message, status: err.status, url: err.url || undefined, method };
}

/* =========================
   ZONE HELPER
   ========================= */
declare const Zone: any;
// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
const isNgZoneEnabled = typeof Zone !== 'undefined' && Zone.root?.run;

export function runOutsideAngular<T>(callback: () => T): T {
  // Run in root context so Angular's own processes stay smooth
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  return isNgZoneEnabled ? Zone.root.run(callback) : callback();
}

/* =========================
   PROVIDER
   ========================= */
export function providePostHogErrorHandler(): Provider {
  return {
    provide: ErrorHandler,
    useClass: PostHogErrorHandler, // prefer useClass so DI can construct it
  };
}
